/**
 * components/committee/DecisionItem — one committee decision in the Overview feed.
 * Collapsed: verdict badge + symbol/timeframe + setup + the arbiter's confidence and a
 * relative time. Expanded (button-toggled, keyboard-accessible): the full slate of role
 * votes and the gate results that produced the verdict — so an operator can audit *why*
 * the committee entered, waited, or rejected.
 */
import { useState } from 'react';

import type { DecisionRecord, FinalVerdict, RoleVote } from '../../api/types';
import { ago } from '../../lib/format';

const VERDICT_BADGE: Record<FinalVerdict, string> = {
  ENTER: 'badge--enter',
  REJECT: 'badge--reject',
  WAIT: 'badge--wait',
};

/** Preferred display order; the arbiter (the decider) always reads last. */
const ROLE_ORDER = ['signal', 'bear', 'risk', 'news', 'historian', 'arbiter'] as const;

const ROLE_LABEL: Record<string, string> = {
  signal: 'Signal',
  bear: 'Bear',
  risk: 'Risk',
  news: 'News',
  historian: 'Historian',
  arbiter: 'Arbiter',
};

function orderedVotes(votes: DecisionRecord['votes']): [string, RoleVote][] {
  const known = ROLE_ORDER.filter((r) => votes[r]).map((r) => [r, votes[r]!] as [string, RoleVote]);
  const extra = Object.keys(votes)
    .filter((k) => !ROLE_ORDER.includes(k as (typeof ROLE_ORDER)[number]) && votes[k])
    .map((k) => [k, votes[k]!] as [string, RoleVote]);
  return [...known, ...extra];
}

interface DecisionItemProps {
  readonly decision: DecisionRecord;
}

export function DecisionItem({ decision: d }: DecisionItemProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const votes = orderedVotes(d.votes);
  const arbiterConfidence = d.votes.arbiter?.confidence;

  /**
   * A "short-circuited" decision is one the committee resolved at the first (Signal)
   * stage — Signal saw no valid setup, so the later roles and the arbiter never ran.
   * That is a correct, honest outcome (the bot declining to trade), but a lone Signal
   * vote reads as broken/partial data. We detect it (no arbiter vote reached) and label
   * it explicitly so a single vote row is never mistaken for missing committee data.
   */
  const shortCircuit = d.votes.arbiter === undefined;
  const signalVerdict = d.votes.signal?.verdict;

  return (
    <li className={`decision${open ? ' decision--open' : ''}`}>
      <button
        type="button"
        className="decision__summary"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`badge ${VERDICT_BADGE[d.verdict]}`}>{d.verdict}</span>
        <span className="decision__ident">
          <span className="decision__symbol">
            <strong>{d.symbol}</strong> <span className="muted">{d.timeframe}</span>
          </span>
          <span className="decision__meta secondary">
            {shortCircuit ? (
              <>
                <span className="decision__signal">Signal: {signalVerdict ?? 'NO_TRADE'}</span>
                <span className="decision__shortcut"> · committee short-circuited</span>
              </>
            ) : (
              <>
                {d.setupType ?? '—'}
                {d.marketRegime ? ` · ${d.marketRegime}` : ''}
                {arbiterConfidence != null ? ` · arbiter ${arbiterConfidence}%` : ''}
              </>
            )}
          </span>
        </span>
        <span className="decision__time muted num">{ago(Date.parse(d.createdAt))}</span>
        <span className="decision__chevron" aria-hidden>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="decision__detail">
          {shortCircuit && (
            <p className="decision__note secondary">
              The committee short-circuited at the Signal stage — no valid setup was found, so the
              bear, risk, news, historian and arbiter roles did not run. This is an intentional
              “no trade” outcome, not missing data.
            </p>
          )}
          <div className="votes">
            {votes.map(([role, v]) => (
              <div key={role} className={`vote vote--${verdictTone(v.verdict)}`}>
                <span className="vote__role">{ROLE_LABEL[role] ?? role}</span>
                <span className="vote__verdict">{v.verdict}</span>
                {v.confidence != null && <span className="vote__conf num muted">{v.confidence}%</span>}
              </div>
            ))}
          </div>

          {d.gateResults.length > 0 && (
            <div className="gates">
              <span className="gates__label muted">Gates</span>
              <div className="gates__list">
                {d.gateResults.map((g, i) => (
                  <span key={`${g.gate}-${i}`} className={`gate ${g.pass ? 'gate--pass' : 'gate--fail'}`}>
                    <span aria-hidden>{g.pass ? '✓' : '✕'}</span> {g.gate}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Rough sentiment of a free-text role verdict, for the accent only (never sole meaning). */
function verdictTone(verdict: string): 'pos' | 'neg' | 'neutral' {
  const v = verdict.toUpperCase();
  if (/(BUY|OK|CLEAR|FAVORABLE|ENTER|GO|LONG)/.test(v)) return 'pos';
  if (/(SELL|REJECT|BEARISH|ELEVATED|TIGHT|CAUTION|SHORT|BLOCK)/.test(v)) return 'neg';
  return 'neutral';
}
